import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { RefreshCw, Download, Mail, Phone, Calendar, Briefcase, FileText, ArrowLeft, Eye, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminJobService, { AdminJobApplication } from '../../services/admin/jobService';
import DataTable from '../../components/common/DataTable';

const JobApplications: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const jobId = searchParams.get('job_id');

    const [applications, setApplications] = useState<AdminJobApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // View Modal
    const [viewModal, setViewModal] = useState<{ show: boolean; app: AdminJobApplication | null }>({
        show: false,
        app: null
    });

    useEffect(() => {
        fetchApplications();
    }, [jobId]);

    const fetchApplications = async (showToast = false) => {
        try {
            if (showToast) setRefreshing(true);
            else setLoading(true);

            const data = await adminJobService.getAllApplications({ job_id: jobId ?? undefined });
            setApplications(data);

            if (showToast) toast.success('Applications refreshed!');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch applications');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleDownloadResume = (url: string, name: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `resume_${name.replace(/\s+/g, '_')}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    {jobId && (
                        <button
                            onClick={() => navigate('/admin/jobs')}
                            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors border border-transparent hover:border-secondary-200"
                        >
                            <ArrowLeft size={24} className="text-secondary-600" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-3xl font-bold text-secondary-900">Job Applications</h1>
                        <p className="text-secondary-600 mt-1">
                            {jobId ? 'Showing applications for selected job' : 'Manage all candidate applications'}
                        </p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => fetchApplications(true)}
                        disabled={refreshing}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Applications Table */}
            <DataTable
                data={applications}
                loading={loading}
                searchable={true}
                searchPlaceholder="Search candidates by name, email, or job..."
                searchFields={['full_name', 'email', 'job_title']}
                columns={[
                    {
                        header: 'Candidate',
                        accessor: (app) => (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-sm">
                                    {app.full_name.charAt(0)}
                                </div>
                                <div>
                                    <h3 className="font-bold text-secondary-900">{app.full_name}</h3>
                                    <p className="text-xs text-secondary-500 flex items-center gap-1 mt-0.5">
                                        <Mail size={12} /> {app.email}
                                    </p>
                                </div>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Position Applied',
                        accessor: (app) => (
                            <div className="flex items-center gap-2">
                                <Briefcase size={14} className="text-secondary-400" />
                                <span className="text-sm font-medium text-secondary-700">{app.job_title}</span>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Phone',
                        accessor: (app) => (
                            <div className="flex items-center gap-2 text-sm text-secondary-600">
                                <Phone size={14} />
                                {app.phone_number}
                            </div>
                        ),
                    },
                    {
                        header: 'Applied On',
                        accessor: (app) => (
                            <div className="flex items-center gap-2 text-sm text-secondary-500">
                                <Calendar size={14} />
                                {new Date(app.submitted_at).toLocaleDateString()}
                            </div>
                        ),
                        align: 'center',
                        sortable: true,
                    },
                    {
                        header: 'Resume',
                        accessor: (app) => (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleDownloadResume(app.resume_url, app.full_name); }}
                                className="flex items-center justify-center gap-2 px-3 py-1 bg-primary-50 text-primary-700 rounded-lg text-xs font-bold hover:bg-primary-100 transition-all border border-primary-100"
                            >
                                <Download size={14} />
                                Download PDF
                            </button>
                        ),
                        align: 'center',
                    }
                ]}
                renderActions={(app) => (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); setViewModal({ show: true, app }); }}
                            className="p-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                            title="View Application Details"
                        >
                            <Eye size={18} />
                        </button>
                    </div>
                )}
                onRowClick={(app) => setViewModal({ show: true, app })}
            />

            {/* View Application Modal */}
            {viewModal.show && viewModal.app && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                        {/* Modal Header */}
                        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-white relative z-10">
                            <div>
                                <h2 className="text-2xl font-bold text-secondary-900">Application Details</h2>
                                <p className="text-secondary-500 text-sm">Submitted on {new Date(viewModal.app.submitted_at).toLocaleString()}</p>
                            </div>
                            <button
                                onClick={() => setViewModal({ show: false, app: null })}
                                className="p-2 hover:bg-gray-100 rounded-full text-secondary-400 hover:text-secondary-600 transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-8 space-y-8">
                            {/* Candidate Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <label className="text-xs font-bold text-secondary-400 uppercase tracking-widest block">Candidate Information</label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center font-bold">
                                                {viewModal.app.full_name.charAt(0)}
                                            </div>
                                            <div className="text-lg font-bold text-secondary-900">{viewModal.app.full_name}</div>
                                        </div>
                                        <div className="flex items-center gap-3 text-secondary-600 pl-1">
                                            <Mail size={18} className="text-primary-500" />
                                            {viewModal.app.email}
                                        </div>
                                        <div className="flex items-center gap-3 text-secondary-600 pl-1">
                                            <Phone size={18} className="text-primary-500" />
                                            {viewModal.app.phone_number}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-xs font-bold text-secondary-400 uppercase tracking-widest block">Job Information</label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-secondary-900 font-bold bg-secondary-50 p-3 rounded-xl border border-secondary-100">
                                            <Briefcase size={20} className="text-primary-500" />
                                            {viewModal.app.job_title}
                                        </div>
                                        <button
                                            onClick={() => handleDownloadResume(viewModal.app!.resume_url, viewModal.app!.full_name)}
                                            className="w-full flex items-center justify-center gap-2 mt-2 bg-neutral-900 text-white font-bold py-3 rounded-xl hover:bg-primary-600 transition-all shadow-lg"
                                        >
                                            <FileText size={18} />
                                            Download Resume / CV
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Cover Letter */}
                            {viewModal.app.cover_letter && (
                                <div className="space-y-4">
                                    <label className="text-xs font-bold text-secondary-400 uppercase tracking-widest block">Cover Letter</label>
                                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 text-secondary-700 whitespace-pre-wrap italic leading-relaxed">
                                        "{viewModal.app.cover_letter}"
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => setViewModal({ show: false, app: null })}
                                className="px-8 py-3 bg-white border border-gray-300 text-secondary-700 font-bold rounded-xl hover:bg-gray-100 transition-all"
                            >
                                Close
                            </button>
                            <a
                                href={`mailto:${viewModal.app.email}`}
                                className="px-8 py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg flex items-center gap-2"
                            >
                                <Mail size={18} />
                                Reply via Email
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobApplications;
